import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { getTenantSlugFromHost } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';

const API_URL = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3000/api/v1';

/**
 * Path yang tidak dicatat.
 *
 * Lembar cetak dibuka sekali per dokumen dan langsung ditutup lagi;
 * menghitungnya membuat "halaman paling sering dibuka" berisi lembar
 * kwitansi dan rapor, yang benar secara harfiah dan tidak menjawab satu pun
 * pertanyaan yang membuat orang membuka dasbor pengunjung.
 */
const IGNORED_PREFIXES = ['/portal/kwitansi', '/cetak'];
const IGNORED_FRAGMENTS = ['/cetak/', '/print'];

/**
 * Melaporkan perpindahan halaman di dalam aplikasi.
 *
 * Aplikasi ini adalah SPA yang disajikan nginx sebagai berkas statis, jadi
 * tidak ada server yang melihat perpindahan halaman di dalamnya — hanya
 * peramban. Karena itu bagian ini memang harus berjalan di klien, berbeda
 * dari situs publik yang dicatat dari sisi server.
 *
 * Akibatnya, dan ini disengaja: bot tidak pernah terhitung di sini. Bot
 * tidak menjalankan JavaScript, dan lagi pula tidak bisa masuk. Angka bot
 * di dasbor datang dari situs publik dan dari API, tempat mereka
 * sesungguhnya datang.
 */
export function useVisitTracking(): void {
  const location = useLocation();
  const tenantSlug = useAuthStore((state) => state.user?.tenantSlug ?? null);

  // Menahan path terakhir yang dilaporkan. React 18 memanggil efek dua kali
  // di StrictMode saat pengembangan; tanpa penjaga ini setiap halaman
  // terhitung dua kali, dan itu tidak akan terlihat sampai angkanya
  // dibandingkan dengan sesuatu.
  const lastReported = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname;

    if (path === lastReported.current || isIgnored(path)) {
      return;
    }

    lastReported.current = path;

    // Subdomain didahulukan atas sesi: halaman masuk dibuka SEBELUM ada
    // sesi, dan justru di situ lembaganya sudah diketahui dari alamatnya.
    const slug = getTenantSlugFromHost() ?? tenantSlug;

    void report({
      surface: 'APP',
      path,
      referrer: document.referrer || undefined,
      tenantSlug: slug ?? undefined,
      ...campaignParams(location.search),
    });
  }, [location.pathname, location.search, tenantSlug]);
}

interface VisitPayload {
  surface: 'APP';
  path: string;
  referrer?: string;
  tenantSlug?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/**
 * Mengirim laporan tanpa menahan apa pun.
 *
 * Memakai `fetch` dengan `keepalive`, BUKAN `navigator.sendBeacon`, meski
 * sendBeacon terlihat lebih tepat untuk keperluan ini. Alasannya CORS:
 * aplikasi berjalan di app.sipaud.com sedangkan API di api.sipaud.com, dan
 * kiriman `application/json` lintas origin mewajibkan permintaan preflight
 * — yang tidak bisa dilakukan sendBeacon. Ia akan mengembalikan `true`
 * seolah berhasil sementara peramban membuang kirimannya, dan pencatatan
 * gagal tanpa satu pun tanda.
 *
 * Yang hilang karena itu kecil: laporan dikirim saat orangnya BERPINDAH
 * halaman, bukan saat menutup tab, jadi halamannya masih hidup dan
 * `keepalive` sudah cukup menjaga kiriman terakhir.
 */
async function report(payload: VisitPayload): Promise<void> {
  try {
    await fetch(`${API_URL}/public/analytics/collect`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    // Sengaja dibisukan. Pencatatan pengunjung tidak boleh memunculkan
    // apa pun di layar orang yang sedang mengerjakan hal lain.
  }
}

function isIgnored(path: string): boolean {
  return (
    IGNORED_PREFIXES.some((prefix) => path.startsWith(prefix)) ||
    IGNORED_FRAGMENTS.some((fragment) => path.includes(fragment))
  );
}

function campaignParams(search: string): Pick<VisitPayload, 'utmSource' | 'utmMedium' | 'utmCampaign'> {
  const params = new URLSearchParams(search);

  return {
    utmSource: params.get('utm_source') ?? undefined,
    utmMedium: params.get('utm_medium') ?? undefined,
    utmCampaign: params.get('utm_campaign') ?? undefined,
  };
}
